import { DueDateCell } from '../../utils/dueDate';
import { invoiceAmounts } from '../../utils/invoiceAmounts';
import { formatRupiah } from '../../utils/printHelper';
import { MAX_BATCH, MIN_BATCH } from '../../utils/voucherBatch';
import { Pagination } from '../Pagination';
import StatusBadge from '../StatusBadge';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Checkbox } from '../ui/checkbox';
import { CreditCard, Eye, FileText, Printer, Trash2, Undo2, Calculator } from 'lucide-react';
import { ExportMenu } from '../ExportMenu';

export function InvoiceProsesBayarTable({ appliedSearch, bankSelections, batchCheck, batchMembers, canBatchVoucher, clearBatch, fetchData, filteredInvoicesProsesBayar, getUmurBadge, getUmurHutang, handleProcessToBank, handleRevertInvoice, invoicePage, invoiceTotal, invoiceTotalPages, openDeleteDialog, openEditDeductions, processingBank, setBankSelections, setBatchKeterangan, setSelectedInvoice, setShowBatchDialog, setShowInvoiceDetail, setShowVoucherDialog, setVoucherKeterangan, setVoucherTarget, toggleBatchMember, user, voucherBatch }) {
  return (
    <>
      {(user?.role === 'account_payable' || user?.role === 'approval' || user?.role === 'admin') && (
        <Card className="border-l-4 border-l-indigo-500" data-testid="invoice-proses-bayar-card">
          <CardHeader>
            <div className="flex items-start justify-between gap-2">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <CreditCard className="h-5 w-5 text-indigo-600" />
                  Invoice Proses Bayar
                </CardTitle>
                <p className="text-sm text-slate-500">Invoice yang sudah siap diproses pembayarannya</p>
              </div>
              <ExportMenu table="proses-bayar" params={{ search: appliedSearch }} />
            </div>
          </CardHeader>
          <CardContent>
            {filteredInvoicesProsesBayar.length > 0 ? (
              <>
                {/* Voucher gabungan — panel terpisah dari panel bank di bawah,
                    dan gate role-nya juga berbeda (AP ikut, karena AP sudah
                    boleh mencetak voucher satuan). */}
                {canBatchVoucher && (
                  <div className="mb-4 p-4 bg-purple-50 rounded-lg border border-purple-200" data-testid="voucher-batch-toolbar">
                    <div className="flex items-start justify-between gap-4 flex-wrap">
                      <div className="space-y-1">
                        <div className="flex items-center gap-2">
                          <FileText className="h-4 w-4 text-purple-700" />
                          <span className="text-sm font-medium text-purple-800">Voucher Gabungan</span>
                          <span className="text-sm text-purple-600" data-testid="voucher-batch-count">
                            ({batchMembers.length}/{MAX_BATCH} dipilih)
                          </span>
                        </div>
                        <p className="text-xs text-purple-700">
                          Tandai {MIN_BATCH}-{MAX_BATCH} invoice dari <strong>supplier, perusahaan, dan rekening/VA yang sama</strong> untuk dicetak jadi satu voucher.
                        </p>
                        {appliedSearch && invoiceTotal > filteredInvoicesProsesBayar.length && (
                          <p className="text-xs text-amber-700" data-testid="voucher-batch-page-warning">
                            Ada {invoiceTotal} invoice cocok, hanya {filteredInvoicesProsesBayar.length} tampil di halaman ini. Pilihan hanya berlaku untuk halaman yang tampil.
                          </p>
                        )}
                        {batchMembers.length >= MIN_BATCH && batchCheck.errors.map((e) => (
                          <p key={e.code} className="text-xs text-red-700" data-testid={`voucher-batch-error-${e.code}`}>
                            {e.message}
                          </p>
                        ))}
                        {batchCheck.warnings.map((w) => (
                          <p key={w.code} className="text-xs text-amber-700">{w.message}</p>
                        ))}
                      </div>
                      <div className="flex gap-2 shrink-0">
                        {batchMembers.length > 0 && (
                          <Button size="sm" variant="outline" onClick={clearBatch} data-testid="voucher-batch-clear">
                            Bersihkan
                          </Button>
                        )}
                        <Button
                          size="sm"
                          className="bg-purple-600 hover:bg-purple-700"
                          onClick={() => { setBatchKeterangan(''); setShowBatchDialog(true); }}
                          disabled={batchMembers.length < MIN_BATCH || !batchCheck.ok}
                          data-testid="voucher-batch-print-btn"
                        >
                          <Printer className="h-4 w-4 mr-2" /> Print Voucher Gabungan
                        </Button>
                      </div>
                    </div>
                  </div>
                )}
                {/* Bank selection buttons for approval users */}
                {(user?.role === 'approval' || user?.role === 'admin') && (
                  <div className="mb-4 p-4 bg-indigo-50 rounded-lg">
                    <div className="flex items-center justify-between">
                      <div className="flex gap-4">
                        <span className="text-sm font-medium text-indigo-700">Pilih Bank untuk Proses:</span>
                        {Object.keys(bankSelections).filter(k => bankSelections[k]).length > 0 && (
                          <span className="text-sm text-indigo-600">
                            ({Object.keys(bankSelections).filter(k => bankSelections[k]).length} dipilih)
                          </span>
                        )}
                      </div>
                      <Button
                        size="sm"
                        className="bg-indigo-600 hover:bg-indigo-700"
                        onClick={() => handleProcessToBank()}
                        disabled={processingBank || Object.keys(bankSelections).filter(k => bankSelections[k]).length === 0}
                        data-testid="process-to-bank-btn"
                      >
                        {processingBank ? 'Memproses...' : 'Proses ke Bank'}
                      </Button>
                    </div>
                  </div>
                )}
                <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="bg-slate-50 text-left">
                      {(user?.role === 'approval' || user?.role === 'admin') && (
                        <th className="px-4 py-3 text-sm font-semibold">Bank</th>
                      )}
                      {/* Kondisi ini WAJIB identik dengan <td> padanannya di
                          tbody, kalau tidak seluruh baris bergeser untuk
                          sebagian role. */}
                      {canBatchVoucher && (
                        <th className="px-4 py-3 text-sm font-semibold">Voucher</th>
                      )}
                      <th className="px-4 py-3 text-sm font-semibold">Invoice Number</th>
                      <th className="px-4 py-3 text-sm font-semibold">Tanggal Invoice</th>
                      <th className="px-4 py-3 text-sm font-semibold">Supplier</th>
                      <th className="px-4 py-3 text-sm font-semibold">Perusahaan</th>
                      <th className="px-4 py-3 text-sm font-semibold">No. Rekening</th>
                      <th className="px-4 py-3 text-sm font-semibold">Bank</th>
                      <th className="px-4 py-3 text-sm font-semibold">Nilai Dibayarkan</th>
                      <th className="px-4 py-3 text-sm font-semibold">Status</th>
                      <th className="px-4 py-3 text-sm font-semibold">Jatuh Tempo</th>
                      <th className="px-4 py-3 text-sm font-semibold">Umur Hutang</th>
                      <th className="px-4 py-3 text-sm font-semibold">Siap Proses</th>
                      <th className="px-4 py-3 text-sm font-semibold">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredInvoicesProsesBayar.map((invoice) => {
                      const umurHari = getUmurHutang(invoice.invoice_date);
                      const umurBadge = getUmurBadge(umurHari);
                      return (
                        <tr key={invoice.id} className="border-b" data-testid={`proses-bayar-row-${invoice.id}`}>
                          {(user?.role === 'approval' || user?.role === 'admin') && (
                            <td className="px-4 py-3">
                              <div className="flex gap-2">
                                <label className="flex items-center gap-1 cursor-pointer">
                                  <Checkbox
                                    checked={bankSelections[invoice.id] === 'BSI'}
                                    onCheckedChange={(checked) => setBankSelections({ ...bankSelections, [invoice.id]: checked ? 'BSI' : undefined })}
                                    className="data-[state=checked]:bg-amber-500 data-[state=checked]:border-amber-500"
                                    data-testid={`bsi-check-invoice-${invoice.id}`}
                                  />
                                  <span className="text-xs font-semibold text-emerald-700">BSI</span>
                                </label>
                                <label className="flex items-center gap-1 cursor-pointer">
                                  <Checkbox
                                    checked={bankSelections[invoice.id] === 'BRI'}
                                    onCheckedChange={(checked) => setBankSelections({ ...bankSelections, [invoice.id]: checked ? 'BRI' : undefined })}
                                    className="data-[state=checked]:bg-amber-500 data-[state=checked]:border-amber-500"
                                    data-testid={`bri-check-invoice-${invoice.id}`}
                                  />
                                  <span className="text-xs font-semibold text-blue-700">BRI</span>
                                </label>
                              </div>
                            </td>
                          )}
                          {canBatchVoucher && (
                            <td className="px-4 py-3">
                              {invoice.voucher_gabungan_id ? (
                                <span
                                  className="inline-block px-2 py-1 rounded text-xs font-semibold bg-purple-100 text-purple-800"
                                  title={`Sudah tergabung: ${invoice.voucher_gabungan_label} (${invoice.voucher_gabungan_count} invoice)`}
                                  data-testid={`voucher-batch-badge-${invoice.id}`}
                                >
                                  GAB {invoice.voucher_gabungan_count}
                                </span>
                              ) : (
                                <label className="flex items-center gap-1 cursor-pointer">
                                  <Checkbox
                                    checked={Boolean(voucherBatch[invoice.id])}
                                    onCheckedChange={(checked) => toggleBatchMember(invoice, checked)}
                                    className="data-[state=checked]:bg-purple-600 data-[state=checked]:border-purple-600"
                                    data-testid={`voucher-batch-check-${invoice.id}`}
                                  />
                                </label>
                              )}
                            </td>
                          )}
                          <td className="px-4 py-3 font-mono text-sm font-semibold">{invoice.invoice_number}</td>
                          <td className="px-4 py-3 text-sm">{new Date(invoice.invoice_date).toLocaleDateString('id-ID')}</td>
                          <td className="px-4 py-3">{invoice.supplier_name}</td>
                          <td className="px-4 py-3">{invoice.company_name || '-'}</td>
                          <td className="px-4 py-3 font-mono text-xs">{invoice.supplier_data?.nomor_rekening || '-'}</td>
                          <td className="px-4 py-3 text-sm">{invoice.supplier_data?.nama_bank || '-'}</td>
                          <td className="px-4 py-3 font-mono">Rp {formatRupiah(invoiceAmounts(invoice).nilaiDibayarkan)}</td>
                          <td className="px-4 py-3"><StatusBadge status={invoice.status} /></td>
                          <td className="px-4 py-3"><DueDateCell invoice={invoice} /></td>
                          <td className="px-4 py-3">
                            <span className={`px-2 py-1 rounded text-xs ${umurBadge.color}`} title={umurBadge.status}>
                              {umurBadge.label}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-xs">
                            {invoice.submitted_to_payment_at ? (
                              <div className="space-y-0.5">
                                <div className="font-mono">{new Date(invoice.submitted_to_payment_at).toLocaleDateString('id-ID')}</div>
                                <div className="text-slate-500">{new Date(invoice.submitted_to_payment_at).toLocaleTimeString('id-ID', { hour: '2-digit', minute: '2-digit' })}</div>
                                <div className="text-slate-400 truncate max-w-[100px]" title={invoice.submitted_to_payment_by_name}>{invoice.submitted_to_payment_by_name}</div>
                              </div>
                            ) : (
                              <span className="text-slate-400">-</span>
                            )}
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex gap-2">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => { setSelectedInvoice(invoice); setShowInvoiceDetail(true); }}
                                data-testid={`view-proses-bayar-${invoice.id}`}
                              >
                                <Eye className="h-4 w-4" />
                              </Button>
                              {/* Print Voucher Pembayaran */}
                              {['account_payable', 'approval', 'admin'].includes(user?.role) && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="border-purple-400 text-purple-700 hover:bg-purple-50"
                                  onClick={() => { setVoucherTarget({ type: 'invoice', data: invoice }); setVoucherKeterangan(''); setShowVoucherDialog(true); }}
                                  data-testid={`print-voucher-proses-bayar-${invoice.id}`}
                                  title="Print Voucher Pembayaran"
                                >
                                  <Printer className="h-4 w-4" />
                                </Button>
                              )}
                              {/* Edit PPh/Admin Bank - AP, Approval, Admin */}
                              {['account_payable', 'approval', 'admin'].includes(user?.role) && invoice.status === 'proses_bayar' && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="border-blue-400 text-blue-700 hover:bg-blue-50"
                                  onClick={() => openEditDeductions('invoice', invoice)}
                                  data-testid={`edit-deductions-${invoice.id}`}
                                  title="Edit PPh & Admin Bank"
                                >
                                  <Calculator className="h-4 w-4" />
                                </Button>
                              )}
                              {/* Revert to previous status */}
                              {['approval', 'admin'].includes(user?.role) && ['proses_bayar', 'bank_process'].includes(invoice.status) && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="border-amber-400 text-amber-700 hover:bg-amber-50"
                                  onClick={() => handleRevertInvoice(invoice.id)}
                                  data-testid={`revert-invoice-${invoice.id}`}
                                  title="Kembalikan ke tahap sebelumnya"
                                >
                                  <Undo2 className="h-4 w-4" />
                                </Button>
                              )}
                              {/* Admin Delete Button */}
                              {user?.role === 'admin' && (
                                <Button
                                  size="sm"
                                  variant="destructive"
                                  onClick={() => openDeleteDialog(invoice, 'invoice_full')}
                                  data-testid={`delete-proses-bayar-${invoice.id}`}
                                  title="Hapus Invoice"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <Pagination page={invoicePage} totalPages={invoiceTotalPages} total={invoiceTotal} onPageChange={(p) => fetchData(p, undefined, undefined)} />
              </>
            ) : (
              <p className="text-slate-500 text-center py-8">Belum ada invoice yang siap diproses</p>
            )}
          </CardContent>
        </Card>
      )}
    </>
  );
}
